/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import React, { useRef, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, convertCssUnit } from '@staffino/primitive-controls';
import Header from './header';
import { TextInputControl, YesNoControl } from './controls';
import { ScrollAnchor, useScrollEffect } from './scroll';
import isEnterPressed from './input-submit';

export default function YesNoQuestionBlock({ question, block }) {
  const { t } = useTranslation();
  const context = useFormContext();

  const YesNoControlComponent = context ? Controller : YesNoControl;
  const TextInputControlComponent = context ? Controller : TextInputControl;

  const score = context?.watch(`${block.id}.score`);
  const scoreError = context?.errors[block.id]?.score;
  const scoreIsEmpty = score === undefined || score === '';
  const required = block.required ?? question.required;
  const scoreValidation = { required };

  const commentRef = useRef(null);
  const commentRequired = block.commentRequired ?? question.commentRequired;
  const commentValidation = {
    required: commentRequired && !scoreIsEmpty,
  };
  const commentError = context?.errors[block.id]?.comment;
  const commentAllowed = block.commentAllowed ?? question.commentAllowed;

  const titleSize = question.extraParams?.titleSize ?? block.titleSize;

  const { scrollToError: scrollToErrorScore } = useScrollEffect(
    `${block.id}.score`,
    score,
  );
  const [enterTriggered, setEnterTriggered] = useState(false);
  const { scrollToError: scrollToErrorComment } = useScrollEffect(
    `${block.id}.comment`,
    enterTriggered && !commentError ? 'validValue' : null,
  );

  return (
    <>
      <ScrollAnchor id={`${block.id}.score`} />
      <Header
        question={question}
        size={titleSize}
        required={required}
        answerErrorMessage={scoreError && t('Please answer this question.')}
      />
      <YesNoControlComponent
        as={YesNoControl}
        name={`${block.id}.score`}
        control={context?.control}
        rules={scoreValidation}
        // invalid={!!commentError}
        backgroundColor={question.colorStyle}
        lowerBoundLabel={question.lowerBoundLabel}
        upperBoundLabel={question.upperBoundLabel}
        lowerBoundScore={question.lowerBoundScore ?? undefined}
        upperBoundScore={question.upperBoundScore ?? undefined}
        reverseButtonOrder={
          question.extraParams?.reverseButtonOrder ?? block.reverseButtonOrder
        }
        width={1}
        maxWidth={convertCssUnit('30rem')}
        onFocus={() => scrollToErrorScore(`${block.id}.score`)}
        marginTop={convertCssUnit(titleSize === 'small' ? '0.75rem' : '2rem')}
        marginLeft="auto"
        marginRight="auto"
        marginBottom={0}
      />

      {commentAllowed && (
        <Box {...(scoreIsEmpty && { opacity: 0, height: 0 })}>
          <ScrollAnchor id={`${block.id}.comment`} focusOn={commentRef} />
          <Header
            size="small"
            required={commentRequired}
            question={{
              title: question.commentTitle || t('Care to tell us more?'),
            }}
            answerErrorMessage={
              commentError && t('Please answer this question.')
            }
            mt={convertCssUnit('2.5rem')}
          />
          <TextInputControlComponent
            blockId={`${block.id}.comment`}
            name={`${block.id}.comment`}
            as={
              <TextInputControl
                ref={(element: any) => {
                  context?.register?.(element, commentValidation);
                  commentRef.current = element;
                }}
              />
            }
            control={context?.control}
            rules={{ required: required && commentRequired }}
            invalid={!!commentError}
            placeholder={question.commentLabel || t('Your feedback')}
            activeColor="primary"
            width={1}
            maxWidth={convertCssUnit('30rem')}
            marginTop={convertCssUnit('0.75rem')}
            marginLeft="auto"
            marginRight="auto"
            marginBottom={0}
            onKeyDown={(event) => setEnterTriggered(isEnterPressed(event))}
            onFocus={() => scrollToErrorComment(`${block.id}.comment`)}
          />
        </Box>
      )}
    </>
  );
}
